import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

export interface Category {
  id: string // UUID
  name: string
  type: 'income' | 'expense'
  icon: string | null
  color: string | null
  is_essential: boolean
  is_active: boolean
  created_at: string
  updated_at: string
  parent_category_id: string | null
}

interface CategoryListResponse {
  categories: Category[]
}

interface CategoryResponse {
  id: string
  name: string
  type: 'income' | 'expense'
  icon: string | null
  color: string | null
  is_essential: boolean
  is_active: boolean
  deleted_at: string | null
  created_at: string
  updated_at: string
  parent_category_id: string | null
}

interface CategoryCreate {
  name: string
  type: 'income' | 'expense'
  icon?: string | null
  color?: string | null
  parent_category_id?: string | null
}

interface CategoryUpdate {
  name?: string
  type?: 'income' | 'expense'
  icon?: string | null
  color?: string | null
  parent_category_id?: string | null
}

// Query para lista de categorias
export function useCategories() {
  return useQuery<CategoryListResponse>({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await api.get<CategoryListResponse>('/v0.1/categories/')
      return res.data
    },
  })
}

// Mutação para criar categoria
export function useCreateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (newCategory: CategoryCreate) => {
      const res = await api.post<CategoryResponse>('/v0.1/categories/', newCategory)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })
}

// Mutação para atualizar categoria
export function useUpdateCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: CategoryUpdate }) => {
      const res = await api.patch<CategoryResponse>(`/v0.1/categories/${id}`, updates)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })
}

// Mutação para arquivar (excluir) categoria
export function useArchiveCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (categoryId: string) => {
      const res = await api.delete(`/v0.1/categories/${categoryId}`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
    },
  })
}

// Mutação para migrar transações para outra categoria
export function useMigrateTransactions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ sourceId, targetId }: { sourceId: string; targetId: string }) => {
      const res = await api.post<CategoryResponse>(`/v0.1/categories/${sourceId}/migrate`, {
        target_category_id: targetId,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}
